import { useState, useEffect } from 'react';
import { Shield, UserCog, Briefcase, UserPlus, X, Send, Trash2 } from 'lucide-react';
import { chProfileService, chProjectService } from '../services';
import { userService } from '../../services/userService';
import { useChAuth } from '../contexts/ChAuthContext';
import { useModal } from '../../contexts/ModalContext';
import type { ChProfile, ChProject, ChUserRole } from '../types';
import { ROLE_LABELS, ROLE_DESCRIPTIONS } from '../types';

type AssignableRole = 'admin' | 'project_manager' | 'client';

const ROLE_CONFIG: { value: AssignableRole; icon: typeof Shield; color: string }[] = [
  { value: 'admin', icon: Shield, color: 'bg-red-50 text-red-700' },
  { value: 'project_manager', icon: UserCog, color: 'bg-blue-50 text-blue-700' },
  { value: 'client', icon: Briefcase, color: 'bg-amber-50 text-amber-700' },
];

// A Content-Hub-only invite carries no main-app permissions
const NO_MAIN_APP_PERMISSIONS: Record<string, boolean> = {
  can_view_campaigns: false,
  can_edit_campaigns: false,
  can_view_clients: false,
  can_edit_clients: false,
  can_view_influencers: false,
  can_edit_influencers: false,
  can_view_payments: false,
  can_manage_users: false,
};

function inviteErrorMessage(err: unknown): string {
  const body = (err as { context?: { body?: { error?: string; actionableSteps?: string[] } } })?.context?.body;
  const main = body?.error || (err instanceof Error ? err.message : '') || 'Não foi possível enviar o convite';
  const step = body?.actionableSteps?.[0];
  return step && step !== main ? `${main}. ${step}` : main;
}

export function ChUsersPage() {
  const { user: me } = useChAuth();
  const { toast, showConfirm } = useModal();
  const [users, setUsers] = useState<ChProfile[]>([]);
  const [projects, setProjects] = useState<ChProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [sending, setSending] = useState(false);
  const [invite, setInvite] = useState<{ email: string; name: string; role: AssignableRole; projectIds: string[] }>({
    email: '',
    name: '',
    role: 'client',
    projectIds: [],
  });

  useEffect(() => {
    loadUsers();
    chProjectService.getAll()
      .then(all => setProjects(all.filter(p => !p.archived)))
      .catch(() => setProjects([]));
  }, []);

  async function loadUsers() {
    try {
      setLoading(true);
      const data = await chProfileService.getAll();
      setUsers(data);
    } catch {
      toast.error('Erro ao carregar os utilizadores');
    } finally {
      setLoading(false);
    }
  }

  async function handleRoleChange(userId: string, role: ChUserRole) {
    try {
      await chProfileService.updateRole(userId, role);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role } : u));
      toast.success('Papel atualizado. Aplica-se no próximo login da pessoa.');
    } catch {
      toast.error('Erro ao alterar o papel do utilizador');
    }
  }

  async function handleRemoveAccess(target: ChProfile) {
    const confirmed = await showConfirm({
      title: 'Remover acesso ao Content Hub?',
      message: `${target.name} deixa de entrar no Content Hub. Os comentários e aprovações que fez ficam guardados.`,
      confirmText: 'Remover acesso',
      cancelText: 'Cancelar',
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      await chProfileService.removeAccess(target.id);
      setUsers(prev => prev.filter(u => u.id !== target.id));
      toast.success('Acesso removido');
    } catch {
      toast.error('Não foi possível remover o acesso');
    }
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!invite.email.trim()) return;
    if (invite.role === 'client' && invite.projectIds.length === 0) {
      toast.warning('Escolhe pelo menos um projeto para o cliente ver');
      return;
    }
    setSending(true);
    try {
      const result = await userService.sendInvitation(
        invite.email.trim(),
        'content_hub',
        NO_MAIN_APP_PERMISSIONS,
        undefined,
        invite.role,
        invite.role === 'client' ? invite.projectIds : undefined,
        invite.name.trim() || undefined,
      );
      if (result.emailSent) {
        toast.success(`Convite enviado para ${result.email}`);
      } else {
        try { await navigator.clipboard.writeText(result.inviteUrl); } catch { /* clipboard blocked */ }
        toast.warning('Convite criado, mas o email não foi enviado. O link de acesso foi copiado — envia-o tu.', 10000);
      }
      setShowInvite(false);
      setInvite({ email: '', name: '', role: 'client', projectIds: [] });
      await loadUsers();
    } catch (err) {
      toast.error(inviteErrorMessage(err), 10000);
    } finally {
      setSending(false);
    }
  }

  function toggleProject(id: string) {
    setInvite(prev => ({
      ...prev,
      projectIds: prev.projectIds.includes(id)
        ? prev.projectIds.filter(p => p !== id)
        : [...prev.projectIds, id],
    }));
  }

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto ch-animate-in">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <h1 className="ch-heading ch-heading-lg">Utilizadores</h1>
          <span className="ch-chip">{users.length}</span>
        </div>
        <button onClick={() => setShowInvite(true)} className="ch-btn ch-btn-primary ch-btn-sm">
          <UserPlus className="w-3.5 h-3.5" />
          Convidar
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        {ROLE_CONFIG.map(r => (
          <div key={r.value} className="ch-card py-3">
            <span className={`ch-badge ${r.color}`}>
              <r.icon className="w-3 h-3" />
              {ROLE_LABELS[r.value]}
            </span>
            <p className="text-xs text-gray-500 mt-2">{ROLE_DESCRIPTIONS[r.value]}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="ch-card space-y-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <div className="w-10 h-10 rounded-full bg-gray-100" />
              <div className="flex-1">
                <div className="h-4 w-32 bg-gray-100 rounded mb-1" />
                <div className="h-3 w-48 bg-gray-50 rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="ch-card divide-y divide-gray-100">
          {users.map(user => {
            const isMe = user.id === me?.id;
            return (
              <div key={user.id} className="flex items-center gap-4 py-4 first:pt-0 last:pb-0">
                <div className="w-10 h-10 rounded-full bg-[var(--ch-ink)] flex items-center justify-center text-sm font-bold text-[var(--ch-lime)] flex-shrink-0">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[var(--ch-ink)] truncate">
                    {user.name}{isMe && <span className="text-gray-400 font-normal"> (tu)</span>}
                  </p>
                  <p className="text-xs text-gray-400 truncate">{user.email}</p>
                </div>
                <select
                  value={user.role === 'talent' ? 'client' : user.role}
                  onChange={e => handleRoleChange(user.id, e.target.value as ChUserRole)}
                  disabled={isMe}
                  title={isMe ? 'Não podes alterar o teu próprio papel' : undefined}
                  className="ch-select text-xs font-semibold w-auto min-w-[130px] disabled:opacity-60"
                >
                  {ROLE_CONFIG.map(r => (
                    <option key={r.value} value={r.value}>{ROLE_LABELS[r.value]}</option>
                  ))}
                </select>
                {!isMe && (
                  <button
                    onClick={() => handleRemoveAccess(user)}
                    className="p-2 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                    title="Remover acesso"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => !sending && setShowInvite(false)}>
          <form
            onSubmit={handleInvite}
            className="ch-card w-full max-w-md ch-animate-scale space-y-4 max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-[var(--ch-ink)]">Convidar para o Content Hub</h3>
              <button type="button" onClick={() => setShowInvite(false)} className="p-1 rounded-lg hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div>
              <label className="ch-label">Email *</label>
              <input
                type="email"
                required
                autoFocus
                className="ch-input"
                value={invite.email}
                onChange={e => setInvite(prev => ({ ...prev, email: e.target.value }))}
                placeholder="nome@empresa.com"
              />
            </div>

            <div>
              <label className="ch-label">Nome</label>
              <input
                type="text"
                className="ch-input"
                value={invite.name}
                onChange={e => setInvite(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Como aparece nos comentários"
              />
            </div>

            <div>
              <label className="ch-label">Papel</label>
              <div className="space-y-2">
                {ROLE_CONFIG.map(r => (
                  <label
                    key={r.value}
                    className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                      invite.role === r.value ? 'border-[var(--ch-ink)] bg-white' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="ch-invite-role"
                      className="mt-0.5"
                      checked={invite.role === r.value}
                      onChange={() => setInvite(prev => ({ ...prev, role: r.value }))}
                    />
                    <div>
                      <p className="text-sm font-semibold text-[var(--ch-ink)]">{ROLE_LABELS[r.value]}</p>
                      <p className="text-xs text-gray-500">{ROLE_DESCRIPTIONS[r.value]}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {invite.role === 'client' && (
              <div>
                <label className="ch-label">Projetos que vê *</label>
                {projects.length === 0 ? (
                  <p className="text-xs text-gray-400">Ainda não há projetos ativos.</p>
                ) : (
                  <div className="max-h-40 overflow-y-auto space-y-1 p-2 rounded-xl border border-gray-200 bg-white">
                    {projects.map(p => (
                      <label key={p.id} className="flex items-center gap-2 py-1 px-1 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={invite.projectIds.includes(p.id)}
                          onChange={() => toggleProject(p.id)}
                        />
                        <span className="text-sm text-gray-700">{p.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            <p className="text-xs text-gray-400">
              A pessoa recebe um email para definir a password e entra diretamente no Content Hub, sem acesso à app principal.
            </p>

            <button type="submit" disabled={sending || !invite.email.trim()} className="ch-btn ch-btn-primary w-full">
              <Send className="w-4 h-4" />
              {sending ? 'A enviar...' : 'Enviar convite'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
